import { useState, useEffect } from 'react';
import { Customer, CustomerFormData } from '@/types/customer';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useCustomerManagement = () => {
  const { toast } = useToast();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load customers from Supabase
  const loadCustomers = async (forceReload = false) => {
    console.log("🔥 loadCustomers called - forceReload:", forceReload);
    
    // Always set loading when reloading to prevent customers from disappearing
    if (forceReload) {
      setLoading(true);
    }
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('customers')
        .select('id, user_id, first_name, last_name, email, phone, address, notes, customer_number, service_pin, iban, birth_date, created_at, updated_at')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading customers:', error);
        toast({
          title: "Fehler",
          description: "Kunden konnten nicht geladen werden.",
          variant: "destructive",
        });
      } else {
        console.log("🔥 Raw customers data from DB:", data);
      const convertedCustomers = (data || []).map(customer => ({
          id: customer.id,
          first_name: customer.first_name,
          last_name: customer.last_name,
          name: `${customer.first_name} ${customer.last_name || ""}`.trim(),
          birthDate: customer.birth_date || "",
          customerNumber: customer.customer_number || "",
          servicePin: customer.service_pin || "",
          iban: customer.iban || "",
          email: customer.email || "",
          phone: customer.phone || "",
          address: customer.address || "",
          notes: customer.notes || "",
          protocol: [],
          created_at: customer.created_at,
          updated_at: customer.updated_at,
          user_id: customer.user_id
        }));
        console.log("🔥 Converted customers:", convertedCustomers);
        setCustomers(convertedCustomers);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create customer
  const createCustomer = async (formData: CustomerFormData, needsFiles: File[], documentFiles: File[]) => {
    console.log("🔥 createCustomer: Starting with formData:", formData);
    
    if (!formData.firstName || !formData.lastName || !formData.birthDate) {
      console.log("🔥 createCustomer: Validation failed - missing required fields");
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie alle Pflichtfelder aus.",
        variant: "destructive",
      });
      return null;
    }

    console.log("🔥 createCustomer: Validation passed, checking submission state");
    if (isSubmitting) {
      console.log("🔥 createCustomer: Already submitting, returning null");
      return null;
    }
    setIsSubmitting(true);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein, um Kunden zu erstellen.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return null;
    }

    try {
      let verificationToken: string | null = null;
      
      // If email is provided, send opt-in email
      if (formData.email && formData.email.trim()) {
        // Generate verification token
        verificationToken = crypto.randomUUID();
        
        // Get organization data
        const { data: profile } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', session.user.id)
          .single();
        
        let organizationName = 'Ihre Organisation';
        if (profile?.organization_id) {
          const { data: org } = await supabase
            .from('organizations')
            .select('name')
            .eq('id', profile.organization_id)
            .single();
          
          if (org?.name) {
            organizationName = org.name;
          }
        }
        
        // Send opt-in email
        console.log("📧 Sende Opt-In-E-Mail für:", formData.email.trim());
        console.log("📧 Daten:", {
          email: formData.email.trim(),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          organizationName,
          verificationToken,
          appUrl: window.location.origin
        });
        
        const { data: emailData, error: emailError } = await supabase.functions.invoke('send-customer-optin', {
          body: {
            email: formData.email.trim(),
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            organizationName,
            verificationToken,
            appUrl: window.location.origin
          }
        });
        
        if (emailError) {
          console.error('❌ Error sending opt-in email:', emailError);
          console.error('❌ Error details:', JSON.stringify(emailError, null, 2));
          toast({
            title: "Fehler",
            description: `Die Bestätigungs-E-Mail konnte nicht versendet werden: ${emailError.message}`,
            variant: "destructive"
          });
          setIsSubmitting(false);
          return null;
        }
        
        console.log("✅ E-Mail erfolgreich gesendet:", emailData);
      }

      const { data, error } = await supabase
        .from('customers')
        .insert({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim() || null,
          phone: formData.phone.trim() || null,
          address: formData.address.trim(),
          notes: formData.notes.trim() || null,
          customer_number: formData.customerNumber.trim() || null,
          service_pin: formData.servicePin.trim() || null,
          iban: formData.iban.trim() || null,
          birth_date: formData.birthDate || null,
          user_id: session.user.id,
          verification_token: verificationToken,
          data_consent_verified: verificationToken ? false : true
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating customer:', error);
        toast({
          title: "Fehler",
          description: `Kunde konnte nicht erstellt werden: ${error.message}`,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return null;
      }

      // Upload files to Supabase Storage
      const allFiles = [...needsFiles, ...documentFiles];
      if (allFiles.length > 0) {
        for (const file of allFiles) {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
          const filePath = `${session.user.id}/${data.id}/${fileName}`;

          // Upload file to storage
          const { error: uploadError } = await supabase.storage
            .from('customer-files')
            .upload(filePath, file);

          if (!uploadError) {
            // Save file info to database
            await supabase
              .from('customer_files')
              .insert({
                customer_id: data.id,
                user_id: session.user.id,
                file_name: file.name,
                file_path: filePath,
                file_type: file.type,
                file_size: file.size
              });
          }
        }
      }

      // Convert to component format and add to state  
      const newCustomer: Customer = {
        id: data.id,
        user_id: data.user_id,
        first_name: data.first_name,
        last_name: data.last_name || "",
        name: `${data.first_name} ${data.last_name || ""}`.trim(),
        email: data.email,
        phone: data.phone,
        address: data.address,
        notes: data.notes,
        customerNumber: data.customer_number || "",
        servicePin: data.service_pin || "",
        iban: data.iban || "",
        birthDate: data.birth_date || "",
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      setCustomers(prev => [newCustomer, ...prev]);

      // Show different message based on email verification
      if (verificationToken) {
        toast({
          title: "Kunde erstellt",
          description: `${newCustomer.name} wurde erstellt. Bitte warten Sie auf die E-Mail-Bestätigung.`,
        });
      } else {
        toast({
          title: "Kunde erstellt",
          description: `${newCustomer.name} wurde erfolgreich erstellt.`,
        });
      }

      setIsSubmitting(false);
      
      // Return customer with verification token if applicable
      return verificationToken ? { ...newCustomer, verificationToken } : newCustomer;
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return null;
    }
  };

  // Update customer
  const updateCustomer = async (customerId: string, formData: CustomerFormData) => {
    console.log("updateCustomer called with:", { customerId, formData });
    
    if (!formData.firstName || !formData.lastName) {
      toast({
        title: "Fehler",
        description: "Bitte füllen Sie alle Pflichtfelder aus.",
        variant: "destructive",
      });
      return false;
    }

    if (isSubmitting) return false;
    setIsSubmitting(true);

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return false;
    }

    try {
      const { data, error } = await supabase
        .from('customers')
        .update({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim() || null,
          phone: formData.phone.trim() || null,
          address: formData.address.trim(),
          notes: formData.notes.trim() || null,
          customer_number: formData.customerNumber.trim() || null,
          service_pin: formData.servicePin.trim() || null,
          iban: formData.iban.trim() || null,
          birth_date: formData.birthDate || null,
        })
        .eq('id', customerId)
        .eq('user_id', session.user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating customer:', error);
        toast({
          title: "Fehler",
          description: `Kunde konnte nicht aktualisiert werden: ${error.message}`,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return false;
      }

      // Update local state
      const updatedCustomer: Customer = {
        id: data.id,
        user_id: data.user_id,
        first_name: data.first_name,
        last_name: data.last_name || "",
        name: `${data.first_name} ${data.last_name || ""}`.trim(),
        email: data.email,
        phone: data.phone,
        address: data.address,
        notes: data.notes,
        customerNumber: data.customer_number || "",
        servicePin: data.service_pin || "",
        iban: data.iban || "",
        birthDate: data.birth_date || "",
        created_at: data.created_at,
        updated_at: data.updated_at
      };

      setCustomers(prev => prev.map(c => c.id === customerId ? updatedCustomer : c));

      toast({
        title: "Kunde aktualisiert",
        description: `${updatedCustomer.name} wurde erfolgreich aktualisiert.`,
      });

      setIsSubmitting(false);
      return true;
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return false;
    }
  };

  // Delete customer
  const deleteCustomer = async (customerId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      toast({
        title: "Fehler",
        description: "Sie müssen angemeldet sein.",
        variant: "destructive",
      });
      return false;
    }

    try {
      // Delete customer files from storage and database
      const { data: files } = await supabase
        .from('customer_files')
        .select('file_path')
        .eq('customer_id', customerId)
        .eq('user_id', session.user.id);

      if (files && files.length > 0) {
        // Delete files from storage
        const filePaths = files.map(f => f.file_path);
        await supabase.storage
          .from('customer-files')
          .remove(filePaths);

        // Delete file records from database
        await supabase
          .from('customer_files')
          .delete()
          .eq('customer_id', customerId)
          .eq('user_id', session.user.id);
      }

      // Delete customer
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customerId)
        .eq('user_id', session.user.id);

      if (error) {
        console.error('Error deleting customer:', error);
        toast({
          title: "Fehler",
          description: "Kunde konnte nicht gelöscht werden.",
          variant: "destructive",
        });
        return false;
      }

      // Update local state
      setCustomers(prev => prev.filter(c => c.id !== customerId));

      toast({
        title: "Kunde gelöscht",
        description: "Der Kunde wurde erfolgreich gelöscht.",
      });

      return true;
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Add protocol entry
  const addProtocolEntry = async (customerId: string, note: string) => {
    if (!note.trim()) return false;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return false;

      const customer = customers.find(c => c.id === customerId);
      if (!customer) return false;

      const currentNotes = customer.notes || "";
      const newNote = `${new Date().toLocaleString('de-DE')}: ${note}`;
      const updatedNotes = currentNotes ? `${currentNotes}\n\n${newNote}` : newNote;

      const { error } = await supabase
        .from('customers')
        .update({ notes: updatedNotes })
        .eq('id', customerId)
        .eq('user_id', session.user.id);

      if (error) {
        console.error('Error updating notes:', error);
        toast({
          title: "Fehler",
          description: "Notiz konnte nicht gespeichert werden.",
          variant: "destructive",
        });
        return false;
      }

      // Update local state
      const updatedCustomer = { ...customer, notes: updatedNotes };
      setCustomers(prev => prev.map(c => c.id === customerId ? updatedCustomer : c));

      toast({
        title: "Notiz gespeichert",
        description: "Die Notiz wurde erfolgreich hinzugefügt.",
      });

      return true;
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
      return false;
    }
  };

  // Initialize
  useEffect(() => {
    loadCustomers();
  }, []);

  return {
    customers,
    loading,
    isSubmitting,
    setCustomers,
    loadCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    addProtocolEntry
  };
};